import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, PenLine, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import PolicyReader from "./PolicyReader";
import { useAuth } from "../lib/auth";
import { useApi, Panel, StatusChip, btn, inputCls } from "./kit";

export function DueChip({ x }) {
  if (x.acknowledged) return <StatusChip status="completed" label={`Acknowledged ${fmtDate(x.acknowledged.at)}`} />;
  return x.overdue ? <StatusChip status="overdue" label={`Overdue · was due ${fmtDate(x.due_date)}`} /> : <StatusChip status="not_started" label={`Acknowledge by ${fmtDate(x.due_date)}`} />;
}

export function PolicyAckCard({ x, reload }) {
  const { user } = useAuth();
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/policies/${x.policy_id}/acknowledge`, { checked, typed_name: name }); toast.success("Acknowledgement recorded in your records."); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (x.acknowledged) return <p className="text-sm text-muted-foreground" data-testid={`pack-done-${x.policy_id}`}>Version {x.acknowledged.policy_version} acknowledged {fmtDateTime(x.acknowledged.at)} as “{x.acknowledged.typed_name}”.</p>;
  return (
    <div className="space-y-4" data-testid={`pack-card-${x.policy_id}`}>
      <button onClick={() => setReading(true)} className={btn.ghost} data-testid={`pack-open-${x.policy_id}`}><FileText className="h-4 w-4" /> {x.read ? `Read again (v${x.version.version})` : `Read version ${x.version.version}`}</button>
      {reading && <PolicyReader x={x} onClose={() => setReading(false)} onRead={reload} />}
      <label className="flex items-start gap-3 text-[15px]">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={!x.read}
          className="mt-1 h-5 w-5 accent-[color:var(--dcbc-red)]" data-testid={`pack-check-${x.policy_id}`} />
        <span>{x.wording}</span>
      </label>
      <div>
        <label htmlFor={`pname-${x.policy_id}`} className="text-sm font-semibold">Type your full name: <span className="font-normal text-muted-foreground">{user.name}</span></label>
        <input id={`pname-${x.policy_id}`} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} disabled={!x.read} data-testid={`pack-name-${x.policy_id}`} />
      </div>
      {!x.read && <p className="text-sm text-amber-800">Read the policy to the end to unlock the acknowledgement.</p>}
      <button className={btn.primary} disabled={busy || !checked || !name || !x.read} onClick={submit} data-testid={`pack-submit-${x.policy_id}`}><PenLine className="h-4 w-4" /> Acknowledge</button>
    </div>
  );
}

export function HomePolicies() {
  const { data } = useApi("/my/policies");
  const todo = (data || []).filter((x) => !x.acknowledged);
  if (!todo.length) return null;
  return (
    <Panel title="Policies to acknowledge" testid="home-policies">
      <ul className="divide-y">{todo.map((x) => (
        <li key={x.policy_id} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <span className="flex items-center gap-3"><ScrollText className="h-5 w-5 text-[color:var(--dcbc-red)]" /><span><span className="font-medium">{x.title}</span><span className="block text-sm text-muted-foreground">Version {x.version.version}</span></span></span>
          <span className="flex items-center gap-2"><DueChip x={x} /><Link to={`/policies#p-${x.policy_id}`} className={btn.dark} data-testid={`home-policy-${x.policy_id}`}>Review & sign</Link></span>
        </li>))}</ul>
    </Panel>
  );
}
